import { useCallback, useMemo, useState } from 'react'
import * as React from 'react'
import { useIntl } from 'react-intl'
import enrolLoyaltyAccount from 'actions/loyalty/enrolLoyaltyAccount'
import { dismissLoyaltyMembershipActivationBanner, isLoyaltyMembershipActivationBannerDismissed } from 'actions/loyalty/membershipActivationBannerStorage'
import LoyaltyTierLabel from 'components/Common/Labels/LoyaltyTierLabel'
import TextLink from 'LuxKit/TextLink'
import Colour from 'components/utils/Colour'
import Group from 'components/utils/Group'
import { useAppDispatch, useAppSelector } from 'hooks/reduxHooks'
import useLoyaltyUserEligibilityTier from 'loyalty/hooks/useLoyaltyUserEligibilityTier'
import getLoyaltyTierDisplayName from 'loyalty/selectors/getLoyaltyTierDisplayName'
import { useScreenSizeUp } from 'hooks/useScreenSize'
import getCanLoyaltyEnrollExistingUser from 'loyalty/selectors/getCanLoyaltyEnrollExistingUser'
import getTasteOfGoldPromoCampaign from 'takeovers/tasteOfGoldTakeover/selectors/getTasteOfGoldPromoCampaign'
import LoyaltyProductLogo from '../LoyaltyProductLogo'
import useRebrandVersion from 'hooks/useRebrandVersion'
import type { PageBannerCarouselItem } from 'LuxKit/Banners/types'

function useLoyaltyActivationBannerItem(): PageBannerCarouselItem | null {
  const intl = useIntl()
  const dispatch = useAppDispatch()
  const [accountEligibleTier, accountEligibleTierLoading, accountEligibleTierError] = useLoyaltyUserEligibilityTier()
  const [isDismissed, setIsDismissed] = useState(isLoyaltyMembershipActivationBannerDismissed())
  const givenName = useAppSelector(state => state.auth.account.givenName)
  const isDesktop = useScreenSizeUp('desktop')
  const canLoyaltyEnrollExistingUser = useAppSelector(getCanLoyaltyEnrollExistingUser)
  const isTasteOfGoldActive = !!useAppSelector(getTasteOfGoldPromoCampaign)
  const productLogoHeight = useRebrandVersion() === 'rebrand' ? 18 : 24

  const activateMembership = useCallback(() => {
    dispatch(enrolLoyaltyAccount({ enrolment: 'existingUserRegistration' }))
  }, [dispatch])

  const onDismiss = useCallback(() => {
    dismissLoyaltyMembershipActivationBanner()
    setIsDismissed(true)
  }, [])

  const tierName = accountEligibleTier ? getLoyaltyTierDisplayName(accountEligibleTier) : undefined

  const exclusiveInviteText = intl.formatMessage({
    id: 'UFRPGu89LJqf5bfH3uc6',
    defaultMessage: 'Exclusive Invite',
    description: 'Label for exclusive invite in loyalty membership activation banner',
  })

  const activationMessage = useMemo(() => {
    if (!accountEligibleTier || !tierName) return ''

    if (givenName) {
      return intl.formatMessage(
        {
          id: 'C5Ucbn3CDNKO-bhc0QQE',
          defaultMessage: '{name}, you\u2019ve unlocked <tierStatus>{tierName} status</tierStatus> in our new rewards program. <activateLink>Activate for free</activateLink>',
          description: 'Loyalty membership activation banner message with user name, tier status, and activation link',
        },
        {
          name: givenName,
          tierName,
          tierStatus: (chunks: React.ReactNode) => (
            <Colour key="tier-status" value={`tier-${accountEligibleTier}-light-contrast`} as="b">
              {chunks}
            </Colour>
          ),
          activateLink: (chunks: React.ReactNode) => (
            <TextLink key="activate-link" size="medium" onClick={activateMembership}>
              {chunks}
            </TextLink>
          ),
        },
      )
    }

    return intl.formatMessage(
      {
        id: 'aV4fBntCdUzgqkIYtOxZ',
        defaultMessage: 'You\u2019ve unlocked <tierStatus>{tierName} status</tierStatus> in our new rewards program. <activateLink>Activate for free</activateLink>',
        description: 'Loyalty membership activation banner message with tier status and activation link',
      },
      {
        tierName,
        tierStatus: (chunks: React.ReactNode) => (
          <Colour key="tier-status" value={`tier-${accountEligibleTier}-light-contrast`} as="b">
            {chunks}
          </Colour>
        ),
        activateLink: (chunks: React.ReactNode) => (
          <TextLink key="activate-link" size="medium" onClick={activateMembership}>
            {chunks}
          </TextLink>
        ),
      },
    )
  }, [intl, givenName, tierName, accountEligibleTier, activateMembership])

  return useMemo(() => {
    if (isDismissed || !isDesktop || !canLoyaltyEnrollExistingUser || isTasteOfGoldActive || !accountEligibleTier || accountEligibleTierLoading || accountEligibleTierError || accountEligibleTier === 'platinum') {
      return null
    }

    return {
      id: 'loyalty-activation',
      kind: `loyalty-${accountEligibleTier}` as const,
      onDismiss,
      children: <Group direction="horizontal" gap={12} verticalAlign="center">
        <LoyaltyProductLogo height={productLogoHeight} />
        <LoyaltyTierLabel kind="primary" tier={accountEligibleTier} format="uppercase">{exclusiveInviteText}</LoyaltyTierLabel>
        <span>{activationMessage}</span>
      </Group>,
    }
  }, [isDismissed, isDesktop, canLoyaltyEnrollExistingUser, isTasteOfGoldActive, accountEligibleTier, accountEligibleTierLoading, accountEligibleTierError, onDismiss, exclusiveInviteText, activationMessage, productLogoHeight])
}

export default useLoyaltyActivationBannerItem
